import * as React from "react";

import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import { Filter as FilterIcon } from "@/shared/foundation/icons";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import { usePopupState } from "@/shared/hooks/usePopupState";

import { FilterMenu } from "./FilterMenu";
import type {
  ActiveFilters,
  FilterMenuOutput,
  RoboqlMenuConfig,
} from "./FilterModel";

export interface FiltersProps {
  /**
   * Filterable fields available in the Properties pane. The component
   * does not fetch schema or data itself — the consumer is responsible
   * for assembling this list (typically a static set for built-in
   * fields plus a dynamic set for org-specific custom fields).
   */
  filterDefinitions: FilterDefinition[];

  /**
   * Fired when the user commits a filter. Emits `FilterMenuOutput` so
   * a single handler can route both structured filters and roboql
   * queries — typically `view.commit` from `useResourceView`.
   */
  onFilterSelected?: (output: FilterMenuOutput) => void;

  /**
   * Current committed filter state. Required when `roboql` is provided
   * so the menu can pre-fill the editor and render the conflict
   * banner. Optional when only the Properties pane is wired.
   */
  filters?: ActiveFilters;

  /**
   * When provided, the menu surfaces the RoboQL mode bar + editor.
   * When absent, the menu renders today's Properties-only UI — so
   * existing consumers without a roboql dependency are unaffected.
   */
  roboql?: RoboqlMenuConfig;

  /**
   * When true, the menu surfaces a Metrics tab (a searchable tree that fetches
   * the org's metric definitions itself).
   */
  metrics?: boolean;
}

/**
 * Labeled "Filter" outlined button in the Actions row. Opens the shared
 * `<FilterMenu>` popover (also reached via the inline `+` button in
 * `<ResourceView.ActiveFilters>`); the menu lets the user enter exactly
 * one filter at a time and emits it via `onFilterSelected`. When a
 * `roboql` config is supplied the menu also surfaces a RoboQL pane via
 * the mode bar; without it, only the Properties pane renders.
 */
/**
 * Imperative surface for callers that open this menu on the customer's behalf.
 *
 * A ref rather than a controlled `open` prop: the menu anchors to a trigger
 * inside this component, which a prop-driven caller would need handed back.
 * Only opening is exposed.
 */
export interface FiltersHandle {
  /** Open the menu with `field` already chosen in the Properties pane. */
  openWithField: (field: FilterDefinition) => void;
}

export const Filters = React.forwardRef<FiltersHandle, FiltersProps>(
  function Filters(
    { filterDefinitions, onFilterSelected, filters, roboql, metrics },
    ref,
  ) {
    const popup = usePopupState();
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    // Consumed by `FilterMenu` on open; the pane unmounts with the popover, so
    // this only seeds a fresh mount.
    const [initialField, setInitialField] =
      React.useState<FilterDefinition | null>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        openWithField(field) {
          setInitialField(field);
          if (triggerRef.current !== null) {
            popup.open({ anchorEl: triggerRef.current });
          }
        },
      }),
      [popup],
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (popup.isOpen) {
        popup.close();
      } else {
        // Clicking the button means "pick a field", so clear a previous seed.
        setInitialField(null);
        popup.open({ anchorEl: event.currentTarget });
      }
    };

    return (
      <>
        <RobotoButton
          ref={triggerRef}
          variant="secondary"
          icon={<FilterIcon aria-hidden="true" />}
          onClick={handleClick}
          aria-haspopup="dialog"
          aria-expanded={popup.isOpen}
        >
          Filter
        </RobotoButton>
        <FilterMenu
          open={popup.isOpen}
          anchorEl={popup.anchor}
          onClose={popup.close}
          filterDefinitions={filterDefinitions}
          onCommit={(output) => onFilterSelected?.(output)}
          filters={filters}
          roboql={roboql}
          metrics={metrics}
          initialField={initialField}
        />
      </>
    );
  },
);
